import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Briefcase, MapPin, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const counties = ["All", "Sonoma", "Napa", "Marin", "Lake", "Mendocino"];
const jobTypes = ["All", "Full-Time", "Part-Time", "Internship", "Apprenticeship"];

export default function JobsPage({ user }) {
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("All");
  const [selectedJobType, setSelectedJobType] = useState("All");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, searchTerm, selectedCounty, selectedJobType]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error("Failed to fetch jobs", error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = jobs;

    if (searchTerm) {
      filtered = filtered.filter(
        (job) =>
          job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.trade_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCounty !== "All") {
      filtered = filtered.filter((job) => job.county === selectedCounty);
    }

    if (selectedJobType !== "All") {
      filtered = filtered.filter((job) => job.job_type === selectedJobType);
    }

    setFilteredJobs(filtered);
  };

  const getJobTypeColor = (type) => {
    const colors = {
      "Full-Time": "bg-blue-100 text-blue-700",
      "Part-Time": "bg-green-100 text-green-700",
      "Internship": "bg-purple-100 text-purple-700",
      "Apprenticeship": "bg-orange-100 text-orange-700"
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
          Local Job Opportunities
        </h1>
        <p className="text-gray-600">Discover trades jobs in your area</p>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search by job title, company, or trade..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="job-search-input"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select value={selectedCounty} onValueChange={setSelectedCounty}>
            <SelectTrigger data-testid="county-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {counties.map((c) => (
                <SelectItem key={c} value={c}>{c} County</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedJobType} onValueChange={setSelectedJobType}>
            <SelectTrigger data-testid="job-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {jobTypes.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading jobs...</div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No jobs found matching your criteria</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredJobs.map((job) => (
            <Card
              key={job.id}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-l-4"
              style={{ borderLeftColor: '#4ECDC4' }}
              onClick={() => navigate(`/jobs/${job.id}`)}
              data-testid={`job-card-${job.id}`}
            >
              <CardHeader>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                      {job.title}
                    </CardTitle>
                    <CardDescription className="text-base font-medium text-gray-700">
                      {job.company}
                    </CardDescription>
                  </div>
                  <Badge className={getJobTypeColor(job.job_type)} data-testid="job-type-badge">
                    {job.job_type}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{job.city}, {job.county} County</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Briefcase className="h-4 w-4" />
                    <span>{job.trade_type}</span>
                  </div>
                  {job.pay_range && (
                    <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#FF6B6B' }}>
                      <span>{job.pay_range}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="h-4 w-4" />
                    <span>Posted {formatDistanceToNow(new Date(job.posted_date))} ago</span>
                  </div>
                  {job.source && job.source !== 'local' && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${
                          job.source === 'indeed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          job.source === 'simplyhired' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          job.source === 'craigslist' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                          job.source === 'careerjet' ? 'bg-green-50 text-green-700 border-green-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        {job.source === 'indeed' ? 'Indeed' :
                         job.source === 'simplyhired' ? 'SimplyHired' :
                         job.source === 'craigslist' ? 'Craigslist' :
                         job.source === 'careerjet' ? 'CareerJet' :
                         job.source.charAt(0).toUpperCase() + job.source.slice(1)}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}